/**
 * KPI targets of the organization (the dashed target lines of the charts):
 * per machine, line or machine type in the tenant Doris table `kpi_targets`,
 * read and written by cockpitservice (PC.get_kpi_targets, PC.save_kpi_target).
 * Every user of the organization may change them.
 *
 * Per KPI the machine's own target wins, then its line's, then its machine
 * type's (resolveTarget).
 */

import { orgNatsClient, StatementIds } from './OrgNATSClient';

export type KpiScope = 'machine' | 'line' | 'machine_type';

/** The targets the charts draw. */
export type Kpi = 'efficiency' | 'utilization' | 'rejectRatio' | 'maxDowntime' | 'minMTBF' | 'output';

export const KPIS: Kpi[] = ['efficiency', 'utilization', 'rejectRatio', 'maxDowntime', 'minMTBF', 'output'];

/**
 * efficiency, utilization, reject ratio in percent; max downtime in hours; min
 * MTBF in minutes; output per shift: a machine's (or machine type's) count, for
 * a line its line output (the output machine's count times its "Contains").
 */
export type KpiTargetValues = Partial<Record<Kpi, number>>;

export interface KpiTargetRow {
  scope: KpiScope;
  /** machine sn, line_bk or machine type (bautyp) */
  scopeId: string;
  targets: KpiTargetValues;
  updatedBy?: string;
  updatedAt?: string;
}

/** Where a machine's targets can come from, in priority order. */
export interface TargetSubject {
  machineSn?: string;
  lineBk?: string;
  machineType?: string;
}

export interface ResolvedTarget {
  value: number;
  scope: KpiScope;
}

const COLUMN: Record<Kpi, string> = {
  efficiency: 'target_efficiency',
  utilization: 'target_utilization',
  rejectRatio: 'target_reject_ratio',
  maxDowntime: 'target_max_downtime',
  minMTBF: 'target_min_mtbf',
  output: 'target_output',
};

const key = (scope: KpiScope, id: string) => `${scope}:${id}`;

/** resolveTarget for every KPI (the ones that have a target). */
export function resolveAll(
  index: Map<string, KpiTargetRow>,
  subject: TargetSubject,
): Partial<Record<Kpi, ResolvedTarget>> {
  const out: Partial<Record<Kpi, ResolvedTarget>> = {};
  for (const kpi of KPIS) {
    const r = resolveTarget(index, subject, kpi);
    if (r) out[kpi] = r;
  }
  return out;
}

/** The rows by scope and id, for resolveTarget. */
export function indexTargets(rows: KpiTargetRow[]): Map<string, KpiTargetRow> {
  return new Map(rows.map((r) => [key(r.scope, r.scopeId), r]));
}

/**
 * The target that applies to a machine: its own, else its line's, else its
 * machine type's. Output skips the line: a line's output target is its line
 * output (another unit than each machine's count), so a machine takes its own
 * or its machine type's.
 */
export function resolveTarget(
  index: Map<string, KpiTargetRow>,
  subject: TargetSubject,
  kpi: Kpi,
): ResolvedTarget | null {
  const order: [KpiScope, string | undefined][] = [
    ['machine', subject.machineSn],
    ['line', kpi === 'output' ? undefined : subject.lineBk],
    ['machine_type', subject.machineType],
  ];
  for (const [scope, id] of order) {
    const value = id ? index.get(key(scope, id))?.targets[kpi] : undefined;
    if (typeof value === 'number' && Number.isFinite(value)) return { value, scope };
  }
  return null;
}

/** A row of PC.get_kpi_targets as the cockpit uses it. */
export function toRow(raw: Record<string, unknown>): KpiTargetRow | null {
  const scope = raw.scope;
  const scopeId = raw.scope_id;
  if ((scope !== 'machine' && scope !== 'line' && scope !== 'machine_type') || typeof scopeId !== 'string') {
    return null;
  }
  const targets: KpiTargetValues = {};
  for (const kpi of KPIS) {
    const v = raw[COLUMN[kpi]];
    if (typeof v === 'number' && Number.isFinite(v)) targets[kpi] = v;
  }
  return {
    scope,
    scopeId,
    targets,
    updatedBy: typeof raw.updated_by === 'string' ? raw.updated_by : undefined,
    updatedAt: typeof raw.updated_at === 'string' ? raw.updated_at : undefined,
  };
}

/** All targets of the organization. */
export async function fetchKpiTargets(token: string): Promise<KpiTargetRow[]> {
  const response = await orgNatsClient.request<Record<string, unknown>[]>(
    StatementIds.GET_KPI_TARGETS,
    {},
    token,
    { service: 'cockpitservice' },
  );
  if (!response.success) throw new Error(response.error || 'Failed to load the targets');
  const data = Array.isArray(response.data) ? response.data : [];
  return data.map(toRow).filter((r): r is KpiTargetRow => r !== null);
}

/** Replaces the targets of one machine, line or machine type; no target clears them. */
export async function saveKpiTarget(
  scope: KpiScope,
  scopeId: string,
  targets: KpiTargetValues,
  token: string,
): Promise<void> {
  const parameters: Record<string, unknown> = { scope, scope_id: scopeId };
  for (const kpi of KPIS) parameters[COLUMN[kpi]] = targets[kpi] ?? null;
  const response = await orgNatsClient.request(
    StatementIds.SAVE_KPI_TARGET,
    parameters,
    token,
    { service: 'cockpitservice' },
  );
  if (!response.success) throw new Error(response.error || 'Failed to save the targets');
}
